import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, Building2, Users, Inbox, CreditCard, Settings, BarChart3 } from "lucide-react";
import { TeamWorkload } from "@/components/company/TeamWorkload";
import { AutoRebalanceCard } from "@/components/company/AutoRebalanceCard";
import { TeamPerformance } from "@/components/company/TeamPerformance";
import { AgreementsPanel } from "@/components/AgreementsPanel";
import { SecurityPanel } from "@/components/SecurityPanel";
import { BillingHistory } from "@/components/BillingHistory";
import { DashTabs, useTabParam } from "@/components/DashTabs";
import { LeadsBoard } from "@/components/collector/LeadsBoard";
import { NotifyPrefsCard } from "@/components/collector/NotifyPrefsCard";
import { CompanyBillingCard } from "@/components/company/CompanyBillingCard";
import { CompanyTeamCard, collectorName } from "@/components/company/CompanyTeamCard";
import { CompanyProfileCard } from "@/components/company/CompanyProfileCard";
import { CompanyLogoCard } from "@/components/company/CompanyLogoCard";
import { CompanyLogo } from "@/components/TeamBadge";

const TAB_KEYS = ["collectors", "leads", "performance", "billing", "profile"];

const CollectorFilter = ({ collectors, value, onChange }) => (
  <select data-testid="company-leads-collector-filter" className="input-soft sm:!w-auto text-sm !py-2" value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="">All collectors</option>
    {collectors.map((c) => <option key={c.id} value={c.id}>{collectorName(c)}</option>)}
  </select>
);

const DETAIL_FIELDS = [["contact_name", "contact person"], ["phone", "phone"], ["email", "email"], ["ein", "EIN"], ["address", "address"], ["city", "city"], ["state", "state"], ["zip_code", "zip code"]];

const DetailsBanner = ({ company, onTab, onProfile }) => {
  const missing = DETAIL_FIELDS.filter(([k]) => !(company[k] || "").trim()).map(([, l]) => l);
  if (!missing.length) return null;
  return (
    <div className="mb-6 rounded-2xl border border-[#E9C46A] bg-[#E9C46A]/10 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="company-details-banner">
      <div>
        <h2 className="font-heading text-lg text-[#1E3F33]">Finish your company details</h2>
        <p className="text-sm text-[#5C6B64] mt-1">Still needed: {missing.join(", ")}.</p>
      </div>
      {!onProfile && <button type="button" onClick={onTab} data-testid="company-details-banner-cta" className="btn-primary text-sm">Add company details</button>}
    </div>
  );
};

const Header = ({ company, billing }) => (
  <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
    <div className="flex items-center gap-4">
      <CompanyLogo logo={company.logo_url} name={company.company_name} size="w-16 h-16" testid="company-header-logo" />
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2 inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5"/> Company dashboard</div>
        <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold" data-testid="company-dashboard-name">{company.company_name}</h1>
        <p className="text-sm text-[#5C6B64] mt-1">Manage your collectors, their profiles and customer leads in one place.</p>
      </div>
    </div>
    {company.subscription_active
      ? <span className="badge-verified" data-testid="company-status-badge"><ShieldCheck className="w-3 h-3"/> Pro · {billing.seats} active seat{billing.seats === 1 ? "" : "s"}</span>
      : <span className="text-xs px-3 py-1 rounded-full bg-[#1E3F33]/10 text-[#5C6B64]" data-testid="company-status-badge">Subscription inactive</span>}
  </div>
);

export default function CompanyDashboard() {
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useTabParam(TAB_KEYS, "collectors");

  const load = async () => {
    const [{ data: me }, { data: l }] = await Promise.all([api.get("/company/me"), api.get("/company/leads")]);
    setData(me);
    setLeads(l);
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="container-x py-20 text-[#5C6B64]">Loading…</div>;
  const { company, collectors, invites, billing } = data;
  const setCompany = (patch) => setData((d) => ({ ...d, company: { ...d.company, ...patch } }));
  const shownLeads = filter ? leads.filter((l) => l.collector_id === filter) : leads;
  const tabs = [
    { key: "collectors", label: "Collectors", icon: Users, count: collectors.length },
    { key: "leads", label: "Team Leads", icon: Inbox, count: leads.filter((l) => l.status === "requested").length, alert: true },
    { key: "performance", label: "Performance", icon: BarChart3 },
    { key: "billing", label: "Billing", icon: CreditCard },
    { key: "profile", label: "Company Profile", icon: Settings },
  ];

  return (
    <div className="container-x py-12" data-testid="company-dashboard">
      <Header company={company} billing={billing} />
      <DetailsBanner company={company} onTab={() => setTab("profile")} onProfile={tab === "profile"} />
      <DashTabs tabs={tabs} value={tab} onChange={setTab} testid="company-tabs" />

      {tab === "collectors" && <CompanyTeamCard collectors={collectors} invites={invites} reload={load} />}

      {tab === "leads" && <TeamWorkload version={leads} selected={filter} onSelect={setFilter} onChanged={load} />}
      {tab === "leads" && (
        <LeadsBoard
          leads={shownLeads}
          onChanged={load}
          showCollector
          groupable
          sortable
          canReassign={collectors.length > 1}
          title="Team leads"
          subtitle="Customer requests sent to your collectors. You or the collector can mark contact, release, or reassign a lead from each card."
          toolbar={collectors.length > 1 ? <CollectorFilter collectors={collectors} value={filter} onChange={setFilter} /> : null}
          testid="company-leads-card"
        />
      )}

      {tab === "performance" && <TeamPerformance />}

      {tab === "billing" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CompanyBillingCard company={company} billing={billing} reload={load} />
          <BillingHistory />
        </div>
      )}

      {tab === "profile" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-6">
            <CompanyLogoCard company={company} onChange={(logo_url) => setCompany({ logo_url })} />
            <CompanyProfileCard company={company} onSaved={(d) => setData(d)} />
            <NotifyPrefsCard
              prefs={company.notify_prefs} email={company.email} phone={company.phone} endpoint="/company/notify-prefs"
              onSaved={setCompany} title="Company lead alerts" testid="company-notify-prefs-card"
              desc="How the company account is notified when any of your collectors receives a new customer lead. Each collector also gets their own alerts."
            />
            <AutoRebalanceCard company={company} onChange={setCompany} />
          </div>
          <div className="lg:col-span-5 space-y-6">
            <AgreementsPanel />
            <SecurityPanel />
          </div>
        </div>
      )}
    </div>
  );
}
